/**
 * SC-04: 案件新規登録
 *
 * 新規営業案件を登録する画面
 *
 * @created 2025-11-16
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { salesCasesApi, salesMasterApi, salesApprovalsApi, Approver } from '../../services/salesApi';
import type { SalesPhase, AccountSearchResult, ResourceAllocation } from '../../services/salesApi';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import AsyncAutocomplete from '../../components/Common/AsyncAutocomplete';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';
import RequiredBadge from '../../components/Common/RequiredBadge';
import ResourceAllocationTable from '../../components/Sales/ResourceAllocationTable';
import { ActivityTimeline } from '../../components/Sales/ActivityTimeline';

interface Department {
  department_id: string;
  department_name: string;
}

interface DraftActivity {
  activity_id: number;
  activity_date: string;
  activity_type: string;
  description?: string;
}

const SalesCaseCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, salesPermissions } = useAuth();
  const pageTopRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phases, setPhases] = useState<SalesPhase[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountSearchResult | string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // フォームデータ
  const [caseName, setCaseName] = useState('');
  const [caseOverview, setCaseOverview] = useState('');
  const [status, setStatus] = useState('active');
  const [accountId, setAccountId] = useState<number | ''>('');
  const [serviceDivision, setServiceDivision] = useState('');
  const [endUser, setEndUser] = useState('');
  const [businessCategory, setBusinessCategory] = useState('');
  const [salesPerson, setSalesPerson] = useState('');
  const [currentPhase, setCurrentPhase] = useState<number | ''>('');
  const [initialRegistrationReason, setInitialRegistrationReason] = useState('');
  const [expectedOrderAmount, setExpectedOrderAmount] = useState('');
  const [currentYearPlannedSales, setCurrentYearPlannedSales] = useState('');
  const [grossProfit, setGrossProfit] = useState('');
  const [isGrossProfitManuallyEdited, setIsGrossProfitManuallyEdited] = useState(false);
  const [externalProjectId, setExternalProjectId] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [nextActionMemo, setNextActionMemo] = useState('');

  // 新規取引先登録ダイアログ
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountKana, setNewAccountKana] = useState('');

  // 承認者選択ダイアログ
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [selectedApprover, setSelectedApprover] = useState('');
  const [approversLoading, setApproversLoading] = useState(false);

  // 要員計画データ
  const [resourcesData, setResourcesData] = useState<Partial<ResourceAllocation>[]>([]);
  // 行動履歴（登録前はローカルのみ。一時IDは負の連番）
  const [activities, setActivities] = useState<DraftActivity[]>([]);
  const nextTempActivityIdRef = useRef(-1);
  // 指定年度（変更可能）
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  const [fiscalYear, setFiscalYear] = useState(() => currentYear);

  useEffect(() => {
    fetchPhases();
    fetchDepartments();
    if (user) {
      setSalesPerson(user.name);
    }
  }, [user]);

  const fetchPhases = async () => {
    try {
      const response = await salesMasterApi.getPhases();
      if (response.data.success) {
        setPhases(response.data.data);
        // デフォルトでプリセールスを選択
        const presales = response.data.data.find((p) => p.phase_code === 'PRESALES');
        if (presales) {
          setCurrentPhase(presales.phase_id);
        }
      }
    } catch (err) {
      console.error('フェーズ取得エラー:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/departments', {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setDepartments(data.data || []);
      }
    } catch (err) {
      console.error('部署取得エラー:', err);
    }
  };

  // 取引先選択時の処理（freeSoloで文字列も許可）
  const handleAccountSelect = (account: AccountSearchResult | string | null) => {
    setSelectedAccount(account);
    // オブジェクトの場合はaccount_idをセット、文字列の場合は空にする
    if (account && typeof account !== 'string') {
      setAccountId(account.account_id);
    } else {
      setAccountId('');
    }
  };

  // 新規取引先登録後の処理
  const handleAccountCreated = (accountId: number, accountName: string) => {
    setSelectedAccount({
      account_id: accountId,
      account_name: accountName,
    });
    setAccountId(accountId);
  };

  // 行動履歴（登録前はローカル配列のみ）
  const handleAddActivity = async (activity: {
    activity_date: string;
    activity_type: string;
    description?: string;
  }) => {
    const tempId = nextTempActivityIdRef.current;
    nextTempActivityIdRef.current -= 1;
    setActivities((prev) => [
      ...prev,
      {
        activity_id: tempId,
        activity_date: activity.activity_date,
        activity_type: activity.activity_type,
        description: activity.description,
      },
    ]);
  };

  const handleUpdateActivity = async (
    activityId: number,
    activity: { activity_date: string; activity_type: string; description?: string }
  ) => {
    setActivities((prev) =>
      prev.map((a) =>
        a.activity_id === activityId
          ? {
              ...a,
              activity_date: activity.activity_date,
              activity_type: activity.activity_type,
              description: activity.description,
            }
          : a
      )
    );
  };

  const handleDeleteActivity = async (activityId: number) => {
    setActivities((prev) => prev.filter((a) => a.activity_id !== activityId));
  };

  // プリセールスフェーズかどうかのチェック（他のロジックで使用するため先に定義）
  const isPresalesPhase = useMemo(() => {
    if (!currentPhase) return false;
    const phase = phases.find(p => p.phase_id === currentPhase);
    return phase?.phase_code === 'PRESALES';
  }, [currentPhase, phases]);

  // 実施フェーズかどうかのチェック（外部プロジェクトID必須判定用）
  // カバレッジ、実行、生産移管フェーズでは外部プロジェクトIDが必須
  const isExecutionPhase = useMemo(() => {
    if (!currentPhase) return false;
    const phase = phases.find(p => p.phase_id === currentPhase);
    return ['COVERAGE', 'EXECUTION', 'PRODUCTION_TRANSFER'].includes(phase?.phase_code || '');
  }, [currentPhase, phases]);

  // 要員配置から計算した金額を表示用に算出
  const calculatedFromResources = useMemo(() => {
    let totalSales = 0;
    let totalCost = 0;

    resourcesData.forEach(r => {
      if (r.start_month && r.end_month) {
        const start = new Date(r.start_month);
        const end = new Date(r.end_month);
        // 月数を計算
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
        totalSales += (Number(r.unit_price) || 0) * months;
        totalCost += (Number(r.cost_rate) || 0) * months;
      }
    });

    return { sales: totalSales, cost: totalCost, grossProfit: totalSales - totalCost };
  }, [resourcesData]);

  // 要員配置から最も早い開始月を取得
  const earliestStartMonth = useMemo(() => {
    if (resourcesData.length === 0) return '';

    const startMonths = resourcesData
      .filter(r => r.start_month)
      .map(r => r.start_month as string);

    if (startMonths.length === 0) return '';

    // 最も早い月を取得（YYYY-MM形式でソート可能）
    return startMonths.sort()[0].substring(0, 7); // YYYY-MMに正規化
  }, [resourcesData]);

  // 指定年度の月別予実計算（要員配置から自動計算）
  const monthlyBreakdown = useMemo(() => {
    const months: Array<{
      month: number;
      plannedSales: number;
      plannedCost: number;
      plannedOutsourceRate: number;
      actualSales: number;
      actualCost: number;
      actualOutsourceRate: number;
    }> = [];

    for (let m = 1; m <= 12; m++) {
      let plannedSales = 0;
      let plannedCost = 0;
      let plannedOutsourceCost = 0;

      resourcesData.forEach(r => {
        if (r.start_month && r.end_month) {
          // YYYY-MM or YYYY-MM-DD format から年月を抽出（タイムゾーン問題を回避）
          const startYear = parseInt(r.start_month.substring(0, 4));
          const startMonth = parseInt(r.start_month.substring(5, 7));
          const endYear = parseInt(r.end_month.substring(0, 4));
          const endMonth = parseInt(r.end_month.substring(5, 7));

          // ターゲット年月が範囲内かチェック（YYYYMM形式で比較）
          const targetYearMonth = fiscalYear * 100 + m;
          const startYearMonth = startYear * 100 + startMonth;
          const endYearMonth = endYear * 100 + endMonth;

          if (targetYearMonth >= startYearMonth && targetYearMonth <= endYearMonth) {
            plannedSales += Number(r.unit_price) || 0;
            plannedCost += Number(r.cost_rate) || 0;
            // 外注タイプの場合は外注コストとしてカウント
            if (r.resource_type === 'contractor' || r.resource_type === 'outsource_pool') {
              plannedOutsourceCost += Number(r.cost_rate) || 0;
            }
          }
        }
      });

      const plannedOutsourceRate = plannedCost > 0 ? (plannedOutsourceCost / plannedCost) * 100 : 0;

      months.push({
        month: m,
        plannedSales,
        plannedCost,
        plannedOutsourceRate,
        actualSales: 0,
        actualCost: 0,
        actualOutsourceRate: 0,
      });
    }

    return months;
  }, [resourcesData, fiscalYear]);

  // 指定年度の合計計算
  const yearlyTotals = useMemo(() => {
    const totals = monthlyBreakdown.reduce(
      (acc, m) => ({
        plannedSales: acc.plannedSales + m.plannedSales,
        plannedCost: acc.plannedCost + m.plannedCost,
        actualSales: acc.actualSales + m.actualSales,
        actualCost: acc.actualCost + m.actualCost,
      }),
      { plannedSales: 0, plannedCost: 0, actualSales: 0, actualCost: 0 }
    );
    return {
      ...totals,
      plannedGrossProfit: totals.plannedSales - totals.plannedCost,
      actualGrossProfit: totals.actualSales - totals.actualCost,
    };
  }, [monthlyBreakdown]);

  // 粗利額の自動計算（受注予定額の20%）
  useEffect(() => {
    if (expectedOrderAmount && !isGrossProfitManuallyEdited) {
      const calculatedGrossProfit = parseFloat(expectedOrderAmount) * 0.2;
      setGrossProfit(calculatedGrossProfit.toFixed(0));
    }
  }, [expectedOrderAmount, isGrossProfitManuallyEdited]);

  // プリセールスフェーズの場合、金額情報を自動的に0に設定
  useEffect(() => {
    if (isPresalesPhase) {
      setExpectedOrderAmount('0');
      setCurrentYearPlannedSales('0');
      setGrossProfit('0');
    }
  }, [isPresalesPhase]);
  // 受注予定月はearliestStartMonthから自動計算されるため、状態管理は不要

  // 粗利額の手動変更ハンドラ
  const handleGrossProfitChange = (value: string) => {
    setGrossProfit(value);
    if (value) {
      setIsGrossProfitManuallyEdited(true);
    }
  };

  // 選択中フェーズのおすすめアクション
  const suggestedActions = useMemo(() => {
    if (!currentPhase) return [];
    const phase = phases.find(p => p.phase_id === currentPhase);
    return phase?.suggested_next_actions || [];
  }, [currentPhase, phases]);

  // 当年度期待売上額（自動計算）
  const calculatedYearlyExpectedSales = useMemo(() => {
    if (!currentYearPlannedSales || !currentPhase) return 0;
    const phase = phases.find(p => p.phase_id === currentPhase);
    if (!phase) return 0;
    return parseFloat(currentYearPlannedSales) * ((phase.individual_conversion_rate ?? 0) / 100);
  }, [currentYearPlannedSales, currentPhase, phases]);

  // 期待売上（自動計算）
  const calculatedExpectedContribution = useMemo(() => {
    if (!expectedOrderAmount || !currentPhase) return 0;
    const phase = phases.find(p => p.phase_id === currentPhase);
    if (!phase) return 0;
    return parseFloat(expectedOrderAmount) * ((phase.individual_conversion_rate ?? 0) / 100);
  }, [expectedOrderAmount, currentPhase, phases]);

  const handleCreateAccount = async () => {
    if (!newAccountName) {
      setError('取引先名は必須です');
      return;
    }

    try {
      const response = await salesMasterApi.createAccount({
        account_name: newAccountName,
        account_name_kana: newAccountKana,
      });

      if (response.data.success) {
        setAccountDialogOpen(false);
        const createdAccountName = newAccountName;
        setNewAccountName('');
        setNewAccountKana('');
        handleAccountCreated(response.data.data.account_id, createdAccountName);
        setSuccess('取引先を登録しました');
      }
    } catch (err) {
      console.error('取引先登録エラー:', err);
      setError(extractErrorMessage(err, '取引先の登録に失敗しました'));
    }
  };

  // エラー時にページ先頭へスクロール
  const scrollToTop = () => {
    pageTopRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const setErrorAndScroll = (message: string) => {
    setError(message);
    scrollToTop();
  };

  const validateForm = (): boolean => {
    if (!caseName) {
      setErrorAndScroll('案件名は必須です');
      return false;
    }
    if (!caseOverview) {
      setErrorAndScroll('案件概要は必須です');
      return false;
    }
    if (!accountId) {
      setErrorAndScroll('取引先は必須です');
      return false;
    }
    if (!salesPerson) {
      setErrorAndScroll('営業担当者は必須です');
      return false;
    }
    if (!currentPhase) {
      setErrorAndScroll('営業フェーズは必須です');
      return false;
    }

    // プリセールスフェーズの場合、新規登録理由が必須
    if (isPresalesPhase && !initialRegistrationReason.trim()) {
      setErrorAndScroll('プリセールスフェーズでは新規登録理由が必須です');
      return false;
    }

    // プリセールス以外のフェーズでは要員計画が必須
    if (!isPresalesPhase && resourcesData.length === 0) {
      setErrorAndScroll('プリセールス以外のフェーズでは要員計画の入力が必須です');
      return false;
    }
    // 金額は要員配置から動的計算されるためバリデーション不要

    // 事業カテゴリは必須
    if (!businessCategory) {
      setErrorAndScroll('事業カテゴリは必須です');
      return false;
    }

    // 次回アクションは常に必須
    if (!nextActionDate) {
      setErrorAndScroll('次回アクション予定日は必須です');
      return false;
    }
    if (!nextActionMemo) {
      setErrorAndScroll('次回アクション内容は必須です');
      return false;
    }

    // 実施フェーズの場合、外部プロジェクトIDが必須
    const selectedPhase = phases.find(p => p.phase_id === currentPhase);
    if (['COVERAGE', 'EXECUTION', 'PRODUCTION_TRANSFER'].includes(selectedPhase?.phase_code || '') && !externalProjectId) {
      setErrorAndScroll('実施フェーズでは外部プロジェクトID（原価システム）が必須です');
      return false;
    }

    return true;
  };

  // 承認者一覧取得
  const fetchApprovers = async () => {
    setApproversLoading(true);
    try {
      const response = await salesApprovalsApi.getApprovers();
      if (response.data.success) {
        setApprovers(response.data.data);
      }
    } catch (err) {
      console.error('承認者一覧取得エラー:', err);
    } finally {
      setApproversLoading(false);
    }
  };

  // 登録ボタンクリック時（承認者選択ダイアログを表示）
  const handleSubmitClick = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    // 承認者一覧を取得してダイアログを表示
    await fetchApprovers();
    setApprovalDialogOpen(true);
  };

  // 承認者選択後の登録処理
  const handleSubmit = async () => {
    if (!selectedApprover) {
      setError('承認者を選択してください');
      return;
    }

    setApprovalDialogOpen(false);

    try {
      setLoading(true);

      // フェーズ名を取得
      const selectedPhase = phases.find(p => p.phase_id === currentPhase);
      const phaseName = selectedPhase?.phase_name || '';

      // 取引先名を取得
      const accountName = selectedAccount && typeof selectedAccount !== 'string'
        ? selectedAccount.account_name
        : '';

      // pending_dataを構築（新規案件用）
      // 金額は要員配置から計算、なければ手入力値を使用
      const finalExpectedOrderAmount = resourcesData.length > 0 
        ? calculatedFromResources.sales 
        : parseFloat(expectedOrderAmount) || 0;
      const finalGrossProfit = resourcesData.length > 0 
        ? calculatedFromResources.grossProfit 
        : parseFloat(grossProfit) || 0;

      const pendingData = {
        request_type: 'initial',
        original_values: {}, // 新規案件なので元データなし
        new_values: {
          // 基本情報
          case_name: caseName,
          case_summary: caseOverview,
          account_id: accountId as number,
          account_name: accountName,
          service_department: serviceDivision || '',
          end_user: endUser || '',
          business_category: businessCategory,
          // フェーズ情報
          sales_phase_id: currentPhase,
          sales_phase_name: phaseName,
          // 担当者情報
          assigned_employee_id: user?.employee_id,
          assigned_employee_name: user?.name,
          // 日付情報
          expected_order_date: earliestStartMonth ? `${earliestStartMonth}-01` : '',
          next_action_date: nextActionDate || '',
          next_action_memo: nextActionMemo || '',
          // その他
          external_project_id: externalProjectId || '',
          initial_registration_reason: initialRegistrationReason,
          // 金額情報
          expected_order_amount: finalExpectedOrderAmount,
          gross_profit: finalGrossProfit,
          resource_count: resourcesData.length,
        },
        // 要員配置の詳細情報を含める（承認後に適用される）
        resource_allocations: resourcesData.map(r => ({
          employee_id: r.employee_id || undefined,
          resource_name: r.resource_name || 'TBA',
          resource_type: r.resource_type || 'employee',
          vendor_id: r.vendor_id,
          unit_price: r.unit_price || 0,
          cost_rate: r.cost_rate || 0,
          start_month: r.start_month || `${fiscalYear}-01-01`,
          end_month: r.end_month || `${fiscalYear}-12-01`,
        })),
        change_reason: '',
      };

      // 金額フィールドは要員配置から動的計算されるため送信不要
      const data = {
        case_name: caseName,
        case_summary: caseOverview,
        account_id: accountId as number,
        service_department: serviceDivision || undefined,
        end_user: endUser || undefined,
        business_category: businessCategory || undefined,
        assigned_employee_id: user?.employee_id,
        sales_phase_id: currentPhase as number,
        initial_registration_reason: initialRegistrationReason,
        expected_order_date: earliestStartMonth ? `${earliestStartMonth}-01` : undefined,
        external_project_id: externalProjectId || undefined,
        status: 'active', // 新規案件もactiveで登録（承認状態はapproval_statusで管理）
        next_action_date: nextActionDate || undefined,
        next_action_memo: nextActionMemo || undefined,
        // 承認関連データ
        approval_status: 'new_pending', // 新規案件の承認待ち
        approver_id: selectedApprover,
        pending_data: pendingData,
      };

      const response = await salesCasesApi.createCase(data);

      if (response.data.success) {
        const caseId = response.data.data.case_id;

        // 要員配置の正本は承認時に適用する。登録時は pending_data のみ。

        // 行動履歴は案件採番後に既存APIで保存（登録前はローカルのみ）
        if (activities.length > 0) {
          try {
            for (const act of activities) {
              await salesCasesApi.addCaseActivity(caseId, {
                activity_date: act.activity_date,
                activity_type: act.activity_type,
                description: act.description,
              });
            }
          } catch (actErr) {
            console.error('行動履歴保存エラー:', actErr);
            setErrorAndScroll(
              '案件は登録しましたが、行動履歴の一部または全部の保存に失敗しました。案件編集画面から再入力してください。'
            );
            setLoading(false);
            return;
          }
        }

        setSuccess('案件を登録し、承認依頼を提出しました');
        setTimeout(() => {
          navigate('/sales/kpi'); // 承認ワークフロータブへ遷移
        }, 1500);
      }
    } catch (err) {
      console.error('案件登録エラー:', err);
      setErrorAndScroll(extractErrorMessage(err, '案件の登録に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box ref={pageTopRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => setCancelDialogOpen(true)}>
          キャンセル
        </Button>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        営業案件 新規登録
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            基本情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="案件名" />
              <TextField
                value={caseName}
                onChange={(e) => setCaseName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
            </Box>
            <Box>
              <RequiredFieldLabel label="案件概要" />
              <TextField
                value={caseOverview}
                onChange={(e) => setCaseOverview(e.target.value)}
                multiline
                minRows={20}
                fullWidth
                inputProps={{ maxLength: 2000 }}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">案件状態</Typography>
                <RequiredBadge />
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="商談中"
                  color={status === 'active' ? 'primary' : 'default'}
                  onClick={() => setStatus('active')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="保留"
                  color={status === 'suspended' ? 'warning' : 'default'}
                  onClick={() => setStatus('suspended')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="受注"
                  color={status === 'closed' ? 'success' : 'default'}
                  onClick={() => setStatus('closed')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">事業カテゴリ</Typography>
                <RequiredBadge />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label="SI経由開発"
                  color={businessCategory === 'SI経由開発' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('SI経由開発')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="日本国内開発"
                  color={businessCategory === '日本国内開発' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('日本国内開発')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="商品・サービス販売"
                  color={businessCategory === '商品・サービス販売' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('商品・サービス販売')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="SES"
                  color={businessCategory === 'SES' ? 'primary' : 'default'}
                  onClick={() => setBusinessCategory('SES')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">営業フェーズ</Typography>
                <RequiredBadge />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {phases.map((phase) => (
                  <Chip
                    key={phase.phase_id}
                    label={phase.phase_name}
                    color={currentPhase === phase.phase_id ? 'primary' : 'default'}
                    onClick={() => setCurrentPhase(phase.phase_id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: currentPhase === phase.phase_id ? phase.color_code : undefined,
                      '&:hover': { bgcolor: phase.color_code, opacity: 0.8 },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <RequiredFieldLabel label="新規登録理由" />
              <TextField
                value={initialRegistrationReason}
                onChange={(e) => setInitialRegistrationReason(e.target.value)}
                multiline
                rows={3}
                fullWidth
                inputProps={{ maxLength: 500 }}
                helperText="案件を新規登録する理由を記入してください"
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  外部プロジェクトID（原価システムから取得）
                </Typography>
                {isExecutionPhase && <RequiredBadge />}
              </Box>
              <TextField
                value={externalProjectId}
                onChange={(e) => setExternalProjectId(e.target.value)}
                fullWidth
                helperText={isExecutionPhase ? "カバレッジ・実行・生産移管フェーズでは必須" : "既存案件との紐付けに使用できます"}
              />
            </Box>

            <Box>
              <RequiredFieldLabel label="次回アクション日" />
              <TextField
                type="date"
                value={nextActionDate}
                onChange={(e) => setNextActionDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <RequiredFieldLabel label="次回アクション内容" />
              <TextField
                value={nextActionMemo}
                onChange={(e) => setNextActionMemo(e.target.value)}
                multiline
                rows={2}
                fullWidth
                inputProps={{ maxLength: 200 }}
                helperText="次回予定しているアクション内容を記入"
              />
            </Box>
            {suggestedActions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  おすすめアクション（クリックで入力）
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {suggestedActions.map((action) => (
                    <Chip
                      key={action}
                      label={action}
                      size="small"
                      variant="outlined"
                      onClick={() => setNextActionMemo(prev => prev ? `${prev}、${action}` : action)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 要員計画 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            要員計画（任意）
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            案件に配置予定の要員情報を入力できます。案件登録後も詳細画面から編集可能です。
          </Typography>
          <ResourceAllocationTable
            resources={resourcesData}
            onChange={setResourcesData}
            disabled={false}
            fiscalYear={fiscalYear}
            showAlert={true}
          />
        </CardContent>
      </Card>

      {/* 金額情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">金額情報</Typography>
              {resourcesData.length > 0 && (
                <Chip label="要員配置から自動計算" size="small" color="info" />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {fiscalYears.map((year) => (
                <Chip
                  key={year}
                  label={`${year}年`}
                  size="small"
                  color={fiscalYear === year ? 'primary' : 'default'}
                  variant={fiscalYear === year ? 'filled' : 'outlined'}
                  onClick={() => setFiscalYear(year)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <TextField
                label="受注予定額"
                type="number"
                value={resourcesData.length > 0 ? calculatedFromResources.sales.toString() : expectedOrderAmount}
                onChange={(e) => setExpectedOrderAmount(e.target.value)}
                required={!isPresalesPhase}
                fullWidth
                size="small"
                disabled={resourcesData.length > 0}
                InputProps={{ endAdornment: '円' }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="粗利額"
                type="number"
                value={resourcesData.length > 0 ? calculatedFromResources.grossProfit.toString() : grossProfit}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="受注予定月"
                type="month"
                value={earliestStartMonth || ''}
                fullWidth
                size="small"
                disabled
                InputLabelProps={{ shrink: true }}
                helperText={!earliestStartMonth && !isPresalesPhase ? '要員計画から自動設定' : undefined}
              />
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <TextField
                label="期待売上"
                type="number"
                value={calculatedExpectedContribution.toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label={`${fiscalYear}年予定売上`}
                type="number"
                value={(yearlyTotals?.plannedSales || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年予定粗利`}
                type="number"
                value={(yearlyTotals?.plannedGrossProfit || 0).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
            <Grid item xs={6} sm={1.75}>
              <TextField
                label={`${fiscalYear}年期待売上`}
                type="number"
                value={((yearlyTotals?.plannedSales || 0) * (phases.find(p => p.phase_id === currentPhase)?.individual_conversion_rate || 0) / 100).toFixed(0)}
                fullWidth
                size="small"
                disabled
                InputProps={{ endAdornment: '円', readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* 月別予実テーブル */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            月別予実（{fiscalYear}年）
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>月</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>予定外注率</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実売上</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実原価</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>実外注率</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyBreakdown.map((row) => (
                  <TableRow key={row.month} hover>
                    <TableCell>{row.month}月</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedSales)}</TableCell>
                    <TableCell align="right">{formatCurrencyWithUnit(row.plannedCost)}</TableCell>
                    <TableCell align="right">{row.plannedOutsourceRate.toFixed(0)}%</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualSales > 0 ? formatCurrencyWithUnit(row.actualSales) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? formatCurrencyWithUnit(row.actualCost) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {row.actualCost > 0 ? `${row.actualOutsourceRate.toFixed(0)}%` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* 合計行 */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>合計</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedSales || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrencyWithUnit(yearlyTotals?.plannedCost || 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {(yearlyTotals?.plannedCost || 0) > 0
                      ? `${((monthlyBreakdown.reduce((sum, m) => sum + (m.plannedOutsourceRate * m.plannedCost), 0) / (yearlyTotals?.plannedCost || 1))).toFixed(0)}%`
                      : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualSales || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualSales || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {(yearlyTotals?.actualCost || 0) > 0 ? formatCurrencyWithUnit(yearlyTotals?.actualCost || 0) : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>-</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 営業担当者・取引先情報 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            営業担当者・取引先情報
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <RequiredFieldLabel label="営業担当者" />
              <TextField
                value={salesPerson}
                fullWidth
                disabled
                helperText="現在ログイン中のユーザーが自動設定されます"
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>サービス部門</InputLabel>
              <Select
                value={serviceDivision}
                label="サービス部門"
                onChange={(e) => setServiceDivision(e.target.value)}
              >
                <MenuItem value="">
                  <em>選択してください</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.department_id} value={dept.department_name}>
                    {dept.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flexGrow: 1 }}>
                <RequiredFieldLabel label="取引先" />
                <AsyncAutocomplete<AccountSearchResult>
                  label=""
                  searchApi={salesMasterApi.searchAccounts}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.account_name}
                  value={selectedAccount}
                  onChange={handleAccountSelect}
                  placeholder="取引先名で検索..."
                  freeSolo
                  loadOnOpen
                  isOptionEqualToValue={(option, value) => {
                    // freeSoloが有効な場合、valueが文字列になる可能性があるため型チェックが必要
                    if (typeof option === 'string' || typeof value === 'string') {
                      return false;
                    }
                    return option.account_id === value.account_id;
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.account_id}>
                      <Box>
                        <Typography variant="body2">{option.account_name}</Typography>
                        {option.account_name_kana && (
                          <Typography variant="caption" color="text.secondary">
                            {option.account_name_kana}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                />
              </Box>
              {salesPermissions?.can_manage_accounts && (
                <Button variant="outlined" onClick={() => setAccountDialogOpen(true)} sx={{ whiteSpace: 'nowrap', mt: 1 }}>
                  新規登録
                </Button>
              )}
            </Box>
            <TextField
              label="エンドユーザー"
              value={endUser}
              onChange={(e) => setEndUser(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* 行動履歴（任意・登録前はローカル保持） */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            行動履歴
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            任意です。登録前は画面上のみ保持し、案件登録成功後に保存します。
          </Typography>
          <ActivityTimeline
            activities={activities}
            readOnly={false}
            onAdd={handleAddActivity}
            onUpdate={handleUpdateActivity}
            onDelete={handleDeleteActivity}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RequiredBadge />
        <Typography variant="caption" color="text.secondary">
          は必須項目です
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => setCancelDialogOpen(true)} disabled={loading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmitClick}
          disabled={loading}
        >
          {loading ? '登録中...' : '登録する'}
        </Button>
      </Box>

      {/* 承認者選択ダイアログ */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          承認者を選択してください
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              案件の新規登録には承認が必要です。承認者を選択してください。
            </Typography>
            {approversLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl fullWidth>
                <RequiredFieldLabel label="承認者" />
                <Select
                  value={selectedApprover}
                  onChange={(e) => setSelectedApprover(e.target.value)}
                  displayEmpty
                >
                  {approvers.map((approver) => (
                    <MenuItem key={approver.employee_id} value={approver.employee_id}>
                      {approver.name}
                      {approver.department_name && ` (${approver.department_name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!selectedApprover || loading}
          >
            {loading ? <CircularProgress size={24} /> : '承認依頼を提出'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* キャンセル確認ダイアログ */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>入力内容の破棄</DialogTitle>
        <DialogContent>
          入力内容を破棄して案件一覧に戻りますか？
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>いいえ</Button>
          <Button onClick={() => navigate('/sales/cases')} color="primary">
            はい
          </Button>
        </DialogActions>
      </Dialog>

      {/* 取引先新規登録ダイアログ */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>取引先 新規登録</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <RequiredFieldLabel label="取引先名" />
              <TextField
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                fullWidth
              />
            </Box>
            <TextField
              label="取引先名（カナ）"
              value={newAccountKana}
              onChange={(e) => setNewAccountKana(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleCreateAccount} variant="contained">
            登録
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesCaseCreatePage;
